import ModalSearchLayout from "@/components/layout/ModalSearchLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDriverCode } from "@/hooks/standard/st_driver_code/useDriverCode";
import { DriverCode } from "@/interface/standard/st_driver_code";
import CountBox from "@/components/ui/CountBox";

interface DriverSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: DriverCode) => void;
  initialSearchValue?: string; // 초기 검색어 (기사 코드)
}

export default function DriverSearchModal({ isOpen, onClose, onSelect, initialSearchValue }: DriverSearchModalProps) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allData, setAllData] = useState<DriverCode[]>([]);
  const [tableVer, setTableVer] = useState(0);

  const { data: driverCodeData, isLoading } = useDriverCode({
    page: currentPage,
    size: 20,
    sort: "stDriverCode,desc",
    search: actualSearchValue || undefined,
    employmentStatus: "Y", // 재직 중인 기사만 조회
  });

  // 검색어 변경 시 페이지 초기화 (allData는 비우지 않음 - 새 데이터가 오면 교체됨)
  useEffect(() => {
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue]);

  useEffect(() => {
    if (driverCodeData && driverCodeData.content && driverCodeData.content.length > 0) {
      if (currentPage === 0) {
        setAllData(driverCodeData.content);
        setTotalElements(driverCodeData.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...driverCodeData.content]);
      }
    } else if (driverCodeData && (!driverCodeData.content || driverCodeData.content.length === 0)) {
      setAllData([]);
      setTotalElements(0);
    }
  }, [driverCodeData, currentPage]);

  useEffect(() => {
    if (driverCodeData) {
      setLoadingMore(false);
    }
  }, [driverCodeData]);

  const hasMore = useMemo(() => {
    if (!driverCodeData) return false;
    return !driverCodeData.last && allData.length < totalElements;
  }, [driverCodeData?.last, allData.length, totalElements]);

  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore,
    onDataLoad: async () => {
      if (hasMore && !loadingMore) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchKeyword);
    setTableVer(v => v + 1);
  }, [searchKeyword]);

  useEffect(() => {
    setTableVer(v => v + 1);
  }, [actualSearchValue]);

  const handleRowDoubleClick = useCallback(
    (item: DriverCode) => {
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleConfirm = useCallback(() => {
    if (selectedRows.length !== 1) return;
    const selectedItem = allData[selectedRows[0]];
    onSelect(selectedItem);
    onClose();
  }, [selectedRows, allData, onSelect, onClose]);

  const columns: TableColumn[] = [
    { key: "stDriverCode", header: "코드", render: v => String(v).padStart(4, "0") },
    { key: "stDriverName", header: "기사명", bodyAlign: "left" },
    { key: "vehicleNumber", header: "차량번호", render: () => "" }, // 백엔드 응답에 따라 수정 필요
    { key: "stDriverMobileTel", header: "전화번호" },
    { key: "mobile", header: "Mobile", render: (_, row) => row.stDriverMobileTel || "" },
    { key: "email", header: "E-mail", render: () => "" },
    { key: "stDriverRemark", header: "비고", bodyAlign: "left" },
  ];

  // 모달 열릴 때 초기 데이터 로드
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 상태 초기화 (allData는 유지하여 캐시 활용)
      setSelectedRows([]);
      setCurrentPage(0);
      setLoadingMore(false);
      setTableVer(v => v + 1);

      // initialSearchValue가 있으면 해당 값으로 검색
      if (initialSearchValue) {
        setSearchKeyword(initialSearchValue);
        setActualSearchValue(initialSearchValue);
      } else {
        setSearchKeyword("");
        setActualSearchValue("");
        // 캐시된 데이터가 있으면 바로 표시
        if (driverCodeData?.content) {
          setAllData(driverCodeData.content);
          setTotalElements(driverCodeData.totalElements || 0);
        }
      }
    }
  }, [isOpen, initialSearchValue]);

  if (!isOpen) return null;

  return (
    <ModalSearchLayout isOpen={isOpen} onClose={onClose} title="기사 조회" width="w-[1400px]">
      <div className="h-full flex flex-col">
        {/* 검색 조건 */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <InputBox
                label="검색명"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="검색어를 입력하세요"
                onKeyDown={e => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Button variant="primary" onClick={handleSearch}>
                조회
              </Button>
            </div>
          </div>
        </div>

        {/* 테이블 */}
        <div className="flex-1 min-h-0">
          <Table
            showRowNumber
            highlightOnClick
            key={`driver-search-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            columns={columns}
            data={allData}
            onRowDoubleClick={handleRowDoubleClick}
            onRowSelect={setSelectedRows}
            maxHeight="calc(100vh - 350px)"
            loadingMore={loadingMore}
            loading={isLoading && currentPage === 0}
            emptyMessage="검색 결과가 없습니다."
          />
        </div>

        {/* 하단 컨트롤 */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-300 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CountBox label="조회" count={totalElements} />
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={handleConfirm} disabled={selectedRows.length !== 1}>
              확인
            </Button>
            <Button variant="white" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </div>
    </ModalSearchLayout>
  );
}
